import store from '@/store'
import { getLucraClient, getLucraSdk, destroyLucraClient } from '@/util/lucra/client'

const LAST_LUCRA_USER_KEY = 'lucra_last_user_id'

function waitForEvent(client, name) {
    return new Promise((resolve) => {
        const handler = (payload) => {
            client.off(name, handler)
            resolve(payload)
        }
        client.on(name, handler)
    })
}

// Lucra checks the balance before identity, so an unverified racer is told to add funds
// first, and its deposit screen then refuses them ("invalid status") until they finish KYC.
function isUnverified(client) {
    const status = client.user && client.user.accountStatus
    return !!status && status !== 'VERIFIED'
}

function getStoredLucraUserId() {
    try {
        return localStorage.getItem(LAST_LUCRA_USER_KEY)
    } catch (e) {
        return null
    }
}

function setStoredLucraUserId(userId) {
    try {
        if (userId) {
            localStorage.setItem(LAST_LUCRA_USER_KEY, userId)
        } else {
            localStorage.removeItem(LAST_LUCRA_USER_KEY)
        }
    } catch (e) {
        // ignore
    }
}

// Lucra's own session cookie survives full page reloads (it belongs to their origin, not
// ours), so a different ShiftOS user testing on the same browser would otherwise silently
// inherit whoever last logged into Lucra here. Our own marker of "who logged in last" does
// survive reloads too (localStorage), so use it to force a real Lucra sign-out before a
// different user logs in. Every entry point that can open Lucra (join, wallet, deposit,
// withdraw) must call this first - a gap in any one of them lets a stale session through.
async function ensureFreshLucraSession(containerEl, currentUserId) {
    const lastUserId = getStoredLucraUserId()
    if (!currentUserId || !lastUserId || lastUserId === currentUserId) {
        return
    }
    const { LucraUserNotLoggedIn } = await getLucraSdk()
    const probe = await getLucraClient()
    // Visible, not hidden: Lucra's own logout appears to require the frame to actually be
    // shown to process (its own dialog code shows itself before any auth navigation), so a
    // hidden logout attempt can silently no-op and leave the stale cookie in place.
    probe.open(containerEl, undefined, { hidden: false }).login()
    try {
        await probe.ready
    } catch (e) {
        // Already logged out - nothing to clear.
        await destroyLucraClient()
        return
    }
    // Logged in as someone. Force it out, then re-check on the same frame before trusting
    // it - a single fire-and-forget logout was not reliably taking effect in testing.
    for (let attempt = 0; attempt < 3; attempt++) {
        probe.logout()
        await new Promise((resolve) => setTimeout(resolve, 1200))
        probe.redirect().login()
        try {
            await probe.ready
            // Still resolves as logged in - the previous attempt did not clear it. Retry.
        } catch (e) {
            if (e instanceof LucraUserNotLoggedIn) {
                break
            }
        }
    }
    // Fully close and drop our reference so the next open() below is a genuinely new
    // iframe (a fresh network load), not the same SPA instance holding stale auth state.
    await destroyLucraClient()
}

export default {
    data() {
        return { lucraNeedsDeposit: false }
    },
    computed: {
        cashTournamentsLocationEnabled() {
            // locationSelected is the one location this build's API_LOCATION_GROUP_ID
            // resolves to - always set regardless of login state, so this is the single
            // source of truth for whether cash tournaments show at all on this site.
            const location = store.getters.locationSelected
            return !!(location && location.attributes.cash_tournaments_enabled)
        },
    },
    methods: {
        // The deposit popup is only allowed from a user gesture. When the join flow reaches
        // "insufficient funds" (after a login and API round trip) that gesture is long gone and
        // the browser blocks the popup, so the racer's next click on the join button opens it
        // first, before any awaited work, then the join runs again as normal.
        async $lucraPrepareDeposit() {
            if (!this.lucraNeedsDeposit) {
                return
            }
            this.lucraNeedsDeposit = false
            if (isUnverified(await getLucraClient())) {
                return
            }
            await this.$lucraDeposit()
        },

        async $lucraOpen(containerEl, options = {}) {
            const user = store.getters.userResource
            const phone = user ? user.get('phone') : undefined
            const currentUserId = user ? String(user.id) : null

            await ensureFreshLucraSession(containerEl, currentUserId)

            const client = await getLucraClient()
            client.open(containerEl, phone || undefined, { hidden: !!options.hidden }).login()
            try {
                await client.ready
            } catch (e) {
                const { LucraUserNotLoggedIn } = await getLucraSdk()
                if (e instanceof LucraUserNotLoggedIn) {
                    // A hidden iframe can't be filled out. Reveal it so the customer can
                    // actually complete phone verification, then go back to headless once done.
                    if (options.hidden) {
                        client.show()
                    }
                    await waitForEvent(client, 'loginSuccess')
                    if (options.hidden) {
                        client.hide()
                    }
                } else {
                    throw e
                }
            }
            setStoredLucraUserId(currentUserId)
            return client
        },

        async $lucraLinkUser() {
            const user = store.getters.userResource
            if (!user) {
                return
            }
            const client = await getLucraClient()
            client.sendMessage.userUpdated({
                username: user.get('racer_name') || user.get('username'),
                email: user.get('email'),
                phoneNumber: user.get('phone'),
                firstName: user.get('first_name'),
                lastName: user.get('last_name'),
                address: {
                    address: user.get('address_line_1'),
                    addressCont: user.get('address_line_2'),
                    city: user.get('city'),
                    state: user.get('state'),
                    zip: user.get('zip_code'),
                },
                metadata: { externalId: String(user.id) },
            })
        },

        async $lucraDeposit() {
            const client = await getLucraClient()
            const popup = client.popup().deposit()
            return new Promise((resolve) => {
                popup.onClose((result) => resolve(result))
            })
        },

        async $lucraOpenRoute(containerEl, route = 'wallet') {
            // Open hidden and wait for the iframe to be ready (logged in) before registering
            // any listeners: registering exitLucra is what tells Lucra to show its in-app X,
            // and that message is silently dropped if the iframe hasn't loaded yet, leaving
            // the racer stuck on a screen with no way back to ours.
            const client = await this.$lucraOpen(containerEl, { hidden: true })
            const method = ['wallet', 'deposit', 'withdraw'].indexOf(route) !== -1 ? route : 'wallet'
            const user = store.getters.userResource
            const currentUserId = user ? String(user.id) : null
            return new Promise((resolve) => {
                // Lucra drops the racer on its own home screen once a deposit finishes inside
                // the wallet, which shows no balance and no confirmation. Send them back to the
                // wallet so the result is visible before they close it.
                let sawAddFunds = false
                const onNavigate = (event) => {
                    if (event.page === 'add-funds') {
                        sawAddFunds = true
                    } else if (sawAddFunds && event.page === 'home') {
                        sawAddFunds = false
                        client.redirect().wallet()
                    }
                }
                client.on('navigationEvent', onNavigate)
                const done = () => {
                    //client.off('exitLucra', done)
                    client.off('navigationEvent', onNavigate)
                    const sdkUser = client.user
                    try {
                        client.close()
                    } catch (e) {
                        // ignore
                    }
                    setStoredLucraUserId(currentUserId)
                    resolve(sdkUser || null)
                }
                client.on('exitLucra', done)
                client.redirect()[method]()
                client.show()
            })
        },

        async $lucraCloseRoute() {
            const client = await getLucraClient()
            try {
                client.close()
            } catch (e) {
                // ignore
            }
            // client.close() removes the iframe synchronously, but the browser hasn't
            // necessarily painted that removal yet - awaiting this alone still let a message
            // shown right after come in imperceptibly fast, reading as a flash/glitch rather
            // than a clean transition. Give it a beat to actually settle.
            await new Promise((resolve) => setTimeout(resolve, 250))
        },

        async $lucraJoinTournament(matchupId) {
            const client = await getLucraClient()
            const { LucraApiError, LucraApiErrorCode } = await getLucraSdk()
            try {
                const result = await client.api.joinTournament(matchupId)
                this.lucraNeedsDeposit = false
                return { joined: result && result.matchupId ? result.matchupId : matchupId }
            } catch (e) {
                if (!(e instanceof LucraApiError)) {
                    throw e
                }
                if (e.code === LucraApiErrorCode.unverified) {
                    const dialog = client.dialog().kyc()
                    await waitForEvent(client, 'kycComplete')
                    dialog.close()
                    return this.$lucraJoinTournament(matchupId)
                }
                if (e.code === LucraApiErrorCode.demographicInformationMissing) {
                    const dialog = client.dialog().demographic()
                    await waitForEvent(client, 'demographicComplete')
                    dialog.close()
                    return this.$lucraJoinTournament(matchupId)
                }
                if (e.code === LucraApiErrorCode.locationNeeded) {
                    // Lucra has no cached geo token for this user yet. The location-grant
                    // page needs a user gesture inside the iframe to unlock browser
                    // geolocation, so it's presented as a dialog rather than requested silently.
                    const dialog = client.dialog().locationGrant()
                    await waitForEvent(client, 'locationGranted')
                    dialog.close()
                    return this.$lucraJoinTournament(matchupId)
                }
                if (e.code === LucraApiErrorCode.insufficientFunds) {
                    if (isUnverified(client)) {
                        const dialog = client.dialog().kyc()
                        await waitForEvent(client, 'kycComplete')
                        dialog.close()
                        return this.$lucraJoinTournament(matchupId)
                    }
                    let popup = null
                    try {
                        popup = client.popup().deposit()
                    } catch (blocked) {
                        this.lucraNeedsDeposit = true
                        return { needsDeposit: true }
                    }
                    const deposit = await new Promise((resolve) => popup.onClose(resolve))
                    if (deposit && deposit.toastType === 'success') {
                        return this.$lucraJoinTournament(matchupId)
                    }
                    this.lucraNeedsDeposit = true
                    return { needsDeposit: true }
                }
                if (e.code === LucraApiErrorCode.locationError) {
                    return { locationError: true }
                }
                throw e
            }
        },
    },
}
